import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { EventStatus } from '../../../../utils/EventStatus';

import styles from './EventBox.module.css';

import Button, { ButtonType } from '../../generic/Button/Button';
import { PublicEvent } from '../../../../utils/EventsProperties';
import { subscribeEvent, unsubscribeEvent } from '../../../../utils/api';
import { t } from 'i18next';
import { useBrandingContext } from '../../../../context/BrandingContext';
import { useUser } from '../../../../context/UserContext';
import { getProfilePicture } from '../../../../utils/api';
import EditIcon from '../../../../assets/edit.svg';
import SubscribeIcon from '../../../../assets/subscribe.svg';
import UnsubscribeIcon from '../../../../assets/unsubscribe.svg';

const DEFAULT_PERSONA_IMAGE = '/branding/persona.png';
const DEFAULT_EVENT_IMAGE = '/branding/exemple/image_tuile_event.png';

interface EventBoxProps {
  event: PublicEvent;
  imageURL?: string;
  eventStatus: EventStatus;
  onPublish?: (eventId: string) => void;
  onSubscriptionChange?: () => void;
}

const EventBox = (props: EventBoxProps) => {
  const event = props.event;
  const dateDisplay = new Date(event.startDate);
  const navigate = useNavigate();
  const [nbSubscribe, setNbSubscribe] = useState(event.nbSubscription);
  const { getImageUrl } = useBrandingContext();
  const { user } = useUser();
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [showAllTags, setShowAllTags] = useState<boolean>(false);
  const moreBadgeRef = React.useRef<HTMLDivElement>(null);
  const [visibleTagCount, setVisibleTagCount] = useState<number>(
    props.event.tags.length,
  );
  const [profilePictureUrl, setProfilePictureUrl] = useState<string>(
    DEFAULT_PERSONA_IMAGE,
  );
  const [miniatureURL, setMiniatureUrl] = useState<string>(DEFAULT_EVENT_IMAGE);
  const tagMeasureRef = React.useRef<HTMLDivElement>(null);
  const tagsContainerRef = React.useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const updateVisibleTags = () => {
      const container = tagsContainerRef.current;
      const measurement = tagMeasureRef.current;
      if (!container || !measurement) {
        setVisibleTagCount(props.event.tags.length);
        return;
      }

      const availableWidth = container.clientWidth;
      if (availableWidth <= 0) {
        setVisibleTagCount(props.event.tags.length);
        return;
      }

      const tagElements = Array.from(
        measurement.querySelectorAll<HTMLSpanElement>(`.${styles.tag}`),
      );
      const plusButtons = Array.from(
        measurement.querySelectorAll<HTMLButtonElement>(
          '[data-measure-plus="true"]',
        ),
      );
      const plusWidthByCount = new Map<number, number>();
      plusButtons.forEach((button) => {
        const count = Number(button.dataset.count ?? '0');
        plusWidthByCount.set(count, button.getBoundingClientRect().width);
      });

      const gap = 6;
      let usedWidth = 0;
      let count = 0;

      for (let i = 0; i < tagElements.length; i += 1) {
        const tagWidth = tagElements[i].getBoundingClientRect().width;
        const nextWidth = usedWidth + (count > 0 ? gap : 0) + tagWidth;
        const hiddenCount = props.event.tags.length - (i + 1);
        const plusWidth =
          hiddenCount > 0 ? (plusWidthByCount.get(hiddenCount) ?? 0) : 0;
        const requiredWidth =
          hiddenCount > 0 ? nextWidth + gap + plusWidth : nextWidth;

        if (requiredWidth <= availableWidth) {
          usedWidth = nextWidth;
          count += 1;
        } else {
          break;
        }
      }

      setVisibleTagCount(count);
    };

    const resizeObserver = new ResizeObserver(() => {
      updateVisibleTags();
    });

    if (tagsContainerRef.current) {
      resizeObserver.observe(tagsContainerRef.current);
    }

    window.addEventListener('resize', updateVisibleTags);
    updateVisibleTags();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateVisibleTags);
    };
  }, [props.event.tags]);

  useEffect(() => {
    setNbSubscribe(event.nbSubscription);
  }, [event.nbSubscription]);

  function formatNumber(value: number): string {
    return value < 10 ? `0${value}` : `${value}`;
  }

  const formatDuration = (diffMs: number): string => {
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}min`;
    return `${Math.max(0, minutes)} min`;
  };

  const getEventDisplayTime = (
    startDateRaw: Date | string,
    endDateRaw?: Date | string,
  ): string => {
    const now = new Date();
    const start = new Date(startDateRaw);
    const end = endDateRaw ? new Date(endDateRaw) : null;

    if (now >= start && (!end || now <= end)) return 'Live';
    if (end && now > end)
      return formatDuration(end.getTime() - start.getTime());
    return `In ${formatDuration(start.getTime() - now.getTime())}`;
  };

  useEffect(() => {
    const fetchDefaultLogo = async () => {
      try {
        const url = await getImageUrl('default_miniature_image');
        if (url) setMiniatureUrl(url);
      } catch (error) {
        console.error('Error fetching default logo:', error);
      }
    };
    fetchDefaultLogo();
  }, [getImageUrl]);

  useEffect(() => {
    if (props.imageURL) {
      setMiniatureUrl(props.imageURL);
    }
  }, [props.imageURL]);

  useEffect(() => {
    if (user && event.subscribedUserIds) {
      setIsSubscribed(event.subscribedUserIds.includes(user.id));
    }
  }, [user, event]);

  useEffect(() => {
    const fetchProfilePicture = async () => {
      try {
        if (props.event.creatorId) {
          const url = await getProfilePicture(props.event.creatorId);
          if (
            url?.data &&
            typeof url.data === 'string' &&
            !url.data.includes('miniatureundefined')
          ) {
            setProfilePictureUrl(url.data);
          } else {
            setProfilePictureUrl(DEFAULT_PERSONA_IMAGE);
          }
        } else {
          setProfilePictureUrl(DEFAULT_PERSONA_IMAGE);
        }
      } catch (error) {
        console.error('Error fetching profile picture', error);
        setProfilePictureUrl(DEFAULT_PERSONA_IMAGE);
      }
    };
    fetchProfilePicture();
  }, [props.event.creatorId]);

  const isEventCreator = user && event.creatorId === user.id;
  const eventDisplayTime = getEventDisplayTime(
    props.event.startDate,
    props.event.endDate,
  );

  const editButton = (
    <div className={styles.editContainer}>
      <div className={styles.editButton}>
        <img
          className={styles.editIcon}
          src={EditIcon}
          alt="Modifier"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/events/${event.id}/event-settings`);
          }}
        />
      </div>
    </div>
  );

  const subscribeButton = (
    <div className={styles.subscribeWrapper}>
      {isEventCreator ? (
        <div className={styles.organizerBadge}>
          <span>{`${t('Organizer')}`}</span>
        </div>
      ) : !isSubscribed ? (
        <div className={styles.subscribeContainer}>
          <div className={styles.subscribeButton}>
            <img
              className={styles.editIcon}
              src={SubscribeIcon}
              alt="Register"
              onClick={async (e) => {
                e.stopPropagation();
                if ((await subscribeEvent(event.id)).status === 200) {
                  setNbSubscribe(nbSubscribe + 1);
                  setIsSubscribed(true);
                  props.onSubscriptionChange?.();
                }
              }}
            />
          </div>
        </div>
      ) : (
        <div className={styles.UnsubscribeContainer}>
          <div className={styles.UnsubscribeButton}>
            <img
              className={styles.UnsubscribeIcon}
              src={UnsubscribeIcon}
              alt="Unsubscribe"
              onClick={async (e) => {
                e.stopPropagation();
                if ((await unsubscribeEvent(event.id)).status === 200) {
                  setNbSubscribe(nbSubscribe - 1);
                  setIsSubscribed(false);
                  props.onSubscriptionChange?.();
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );

  const buttonsNotPublished = (
    <div className={styles.centerWrapper}>
      <Button
        label={t('Publish')}
        type={ButtonType.primary}
        onClick={() => props.onPublish && props.onPublish(event.id)}
      />
    </div>
  );

  const buttonsFinished = (
    <div className={styles.centerWrapper}>
      <Button
        label={t('statistics')}
        type={ButtonType.primary}
        onClick={() => navigate(`/events/${event.id}/event-statistics`)}
      />
    </div>
  );

  const buttonsPreview = (
    <div className={styles.subscribeWrapper}>
      <Button label={t('Register')} type={ButtonType.primary} />
      <div className={styles.subscriptionsWrapper}>
        <span className={styles.dot}></span>
      </div>
    </div>
  );

  const handleMoreBadgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowAllTags(!showAllTags);
  };

  const displayedTags = event.tags?.slice(0, visibleTagCount) ?? [];
  const hiddenTags = event.tags?.slice(visibleTagCount) ?? [];

  return (
    <div
      className={styles.previewMiniture}
      data-testid="event-card"
      onClick={() => {
        if (props.eventStatus !== EventStatus.Preview) {
          navigate(`/events/${event.id}/tracks`);
        }
      }}
    >
      <div className={styles.topContainer}>
        <img
          className={styles.imageTuileEventIcon}
          src={miniatureURL}
          alt={event.name}
          onError={(e) => {
            e.currentTarget.src = DEFAULT_EVENT_IMAGE;
            e.currentTarget.onerror = null;
          }}
        />

        {props.event.canBeEditByUser &&
          props.eventStatus !== EventStatus.Preview &&
          editButton}

        {props.eventStatus === EventStatus.Published && subscribeButton}

        <div className={styles.viewsContainer}>
          <div className={styles.viewValue}>
            {`${nbSubscribe} ` + t('registered')}
          </div>
        </div>

        {eventDisplayTime !== 'Live' ? (
          <div className={styles.timeContainer}>
            <div className={styles.timeValue}>{eventDisplayTime}</div>
          </div>
        ) : (
          <div className={styles.timeContainerLive}>
            <div className={styles.timeValueLive}>{eventDisplayTime}</div>
          </div>
        )}
      </div>

      <div className={styles.bottomContainer}>
        <div className={styles.bottomContainerUpperPart}>
          <div className={styles.profilePictureContainer}>
            <img
              className={styles.profilePicture}
              src={profilePictureUrl}
              alt={`${props.event.creator.firstName} ${props.event.creator.lastName}'s profile`}
            />
          </div>

          <div className={styles.textContainer}>
            <div className={styles.titleContainer}>
              <h2 className={styles.title} title={props.event.name}>
                {props.event.name}
              </h2>
            </div>

            <div className={styles.infoContainer}>
              <div className={styles.authorContainer}>
                {props.event.creator.firstName +
                  ' ' +
                  props.event.creator.lastName}
              </div>

              <span className={styles.separator}>•</span>

              <div className={styles.dateContainer}>
                {`${formatNumber(dateDisplay.getDate())}/${formatNumber(
                  dateDisplay.getMonth() + 1,
                )}/${dateDisplay.getFullYear()}`}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.bottomContainerLowerPart}>
          <div className={styles.tagsContainer} ref={tagsContainerRef}>
            {displayedTags.length > 0 && (
              <>
                {displayedTags.map((tag, index) => (
                  <span
                    key={index}
                    className={styles.tag}
                    data-testid="event-card-tag"
                  >
                    {tag.name}
                  </span>
                ))}
              </>
            )}

            {hiddenTags.length > 0 && (
              <div className={styles.tagTriggerWrapper} ref={moreBadgeRef}>
                <button
                  className={styles.tagTriggerButton}
                  onClick={handleMoreBadgeClick}
                  aria-label={`+${hiddenTags.length} tags restants`}
                >
                  <span className={styles.tagCount}>+{hiddenTags.length}</span>
                </button>

                {showAllTags && (
                  <>
                    <div
                      className={styles.tagsPopupOverlay}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAllTags(false);
                      }}
                    />
                    <div className={styles.tagsPopup}>
                      <div className={styles.tagsPopupContent}>
                        {hiddenTags.map((tag, index) => (
                          <span key={index} className={styles.tag}>
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div
            className={styles.tagMeasurementContainer}
            ref={tagMeasureRef}
            aria-hidden="true"
          >
            {props.event.tags.map((tag, index) => (
              <span key={index} className={styles.tag}>
                {tag.name}
              </span>
            ))}
            {Array.from({ length: props.event.tags.length + 1 }, (_, count) => (
              <button
                key={count}
                data-count={count}
                data-measure-plus="true"
                className={styles.tagTriggerButton}
                type="button"
              >
                <span className={styles.tagCount}>+{count}</span>
              </button>
            ))}
          </div>
        </div>

        <div className={styles.buttonsContainer}>
          {props.eventStatus === EventStatus.NotPublished &&
            buttonsNotPublished}
          {props.eventStatus === EventStatus.Finished && buttonsFinished}
          {props.eventStatus === EventStatus.Preview && buttonsPreview}
        </div>
      </div>
    </div>
  );
};

export default EventBox;
